import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { ToastService } from '../../../../../shared/services/toast.service';
import { AuthService } from '../../../../auth-pages/services/auth.service';
import { TicketsConfigApiService } from '../../../services/tickets-config-api.service';
import { TicketCannedResponse } from '../../../models/tickets-config.model';

@Component({
  selector: 'app-tickets-canned-responses-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent],
  templateUrl: './canned-responses-form.component.html'
})
export class TicketsCannedResponsesFormComponent implements OnDestroy {
  @Input() drawerMode = true;
  @Output() saved = new EventEmitter<TicketCannedResponse>();
  @Output() cancelled = new EventEmitter<void>();

  private fb = inject(FormBuilder);
  private configApi = inject(TicketsConfigApiService);
  private authService = inject(AuthService);
  private toast = inject(ToastService);

  isEditing = false;
  itemId: number | null = null;
  saving = false;
  error: string | null = null;
  private originalName = '';

  form: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(150)]],
    bodyText: ['', [Validators.required]],
    bodyHtml: ['', [Validators.required]],
    languageCode: ['es', [Validators.maxLength(8)]],
    scope: ['shared'], // 'shared' o 'private' — convertimos a intIdUserOwner al guardar
    isActive: [true]
  });

  private readonly destroy$ = new Subject<void>();

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  reset(): void {
    this.isEditing = false;
    this.itemId = null;
    this.error = null;
    this.saving = false;
    this.form.reset({
      name: '',
      bodyText: '',
      bodyHtml: '',
      languageCode: 'es',
      scope: 'shared',
      isActive: true
    });
  }

  loadCannedResponse(id: number): void {
    this.isEditing = true;
    this.itemId = id;
    this.configApi.getCannedResponseById(id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (item) => {
        this.form.patchValue({
          name: item.name,
          bodyText: item.bodyText,
          bodyHtml: item.bodyHtml,
          languageCode: item.languageCode || 'es',
          scope: item.intIdUserOwner == null ? 'shared' : 'private',
          isActive: item.isActive
        });
        this.originalName = item.name;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar la plantilla';
      }
    });
  }

  /** Sincroniza bodyHtml a partir de bodyText cuando el usuario sólo escribe texto plano. */
  onBodyTextChange(): void {
    const text = this.form.get('bodyText')?.value || '';
    const currentHtml = this.form.get('bodyHtml')?.value || '';
    // Si el HTML está vacío o es texto plano, regenerar.
    if (!currentHtml || currentHtml === text || !currentHtml.includes('<')) {
      this.form.patchValue({ bodyHtml: text }, { emitEvent: false });
    }
  }

  hasError(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched));
  }

  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.error = null;
    const raw = this.form.value;
    const userId = this.authService.getCurrentUser()?.id;

    const req = {
      name: raw.name,
      bodyHtml: raw.bodyHtml || raw.bodyText,
      bodyText: raw.bodyText,
      languageCode: raw.languageCode || undefined,
      intIdUserOwner: raw.scope === 'private' && userId ? userId : undefined,
      isActive: !!raw.isActive
    };

    const obs = this.isEditing && this.itemId != null
      ? this.configApi.updateCannedResponse(this.itemId, req)
      : this.configApi.createCannedResponse(req);

    obs.pipe(takeUntil(this.destroy$)).subscribe({
      next: (item) => {
        this.saving = false;
        if (this.isEditing) this.toast.updated('Plantilla de respuesta',
          this.originalName !== (raw.name?.trim() || '')
            ? `Se actualizó "${this.originalName}" → "${raw.name?.trim() || ''}".`
            : `Se actualizó "${raw.name?.trim() || ''}" correctamente.`);
        else this.toast.created('Plantilla de respuesta', `Se creó "${raw.name?.trim() || ''}" correctamente.`);
        this.saved.emit(item);
      },
      error: (err) => {
        this.saving = false;
        const msg = err.error?.message || 'Error al guardar la plantilla';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  cancelar(): void {
    this.cancelled.emit();
  }
}
